"use client";

import { useMemo, useState } from "react";
import BlogPostCard from "@/components/blog/BlogPostCard";
import type { BlogPost } from "@/lib/content/blog";

type CategoryFilter = "Kõik" | "PPF" | "Keraamika" | "Toonimine" | "Hooldus";
type SortOrder = "newest" | "oldest";

const filters: CategoryFilter[] = ["Kõik", "PPF", "Keraamika", "Toonimine", "Hooldus"];

const categoryLookup: Record<BlogPost["category"], CategoryFilter> = {
  ppf: "PPF",
  keraamika: "Keraamika",
  toonimine: "Toonimine",
  hooldus: "Hooldus",
};

interface BlogPageClientProps {
  posts: BlogPost[];
}

export default function BlogPageClient({ posts }: BlogPageClientProps) {
  const [activeFilter, setActiveFilter] = useState<CategoryFilter>("Kõik");
  const [sortOrder, setSortOrder] = useState<SortOrder>("newest");
  const [visibleCount, setVisibleCount] = useState(6);

  const filteredPosts = useMemo(() => {
    const normalized = posts.filter((post) => {
      if (activeFilter === "Kõik") {
        return true;
      }

      return categoryLookup[post.category] === activeFilter;
    });

    return [...normalized].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();

      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });
  }, [activeFilter, posts, sortOrder]);

  const visiblePosts = filteredPosts.slice(0, visibleCount);
  const hasMore = visibleCount < filteredPosts.length;

  const handleFilterChange = (filter: CategoryFilter) => {
    setActiveFilter(filter);
    setVisibleCount(6);
  };

  return (
    <>
      <style jsx global>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      <div className="mt-10 flex flex-col gap-4 rounded-sm border border-white/10 bg-secondary/70 p-4 shadow-[0_10px_40px_rgba(0,0,0,0.2)] sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-accent">Artiklid</p>
          <p className="mt-2 text-sm text-text-muted">
            {filteredPosts.length} {filteredPosts.length === 1 ? "artikkel" : "artiklit"} · {activeFilter === "Kõik" ? "Kõik teemad" : activeFilter}
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <label className="flex items-center gap-2 text-sm text-text-muted">
            <span>Sorteeri:</span>
            <select
              value={sortOrder}
              onChange={(event) => setSortOrder(event.target.value as SortOrder)}
              className="rounded-sm border border-white/10 bg-primary px-3 py-2 text-sm text-text-main outline-none transition focus:border-accent"
            >
              <option value="newest">Uusimad</option>
              <option value="oldest">Vanimad</option>
            </select>
          </label>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2 sm:gap-3">
        {filters.map((filter) => {
          const isActive = filter === activeFilter;

          return (
            <button
              key={filter}
              type="button"
              onClick={() => handleFilterChange(filter)}
              className={`rounded-sm px-4 py-2 text-sm font-medium transition-colors duration-300 sm:px-6 sm:py-3 ${
                isActive
                  ? "bg-accent text-primary"
                  : "border border-white/10 bg-primary/70 text-text-main hover:border-accent/40 hover:bg-accent hover:text-primary"
              }`}
            >
              {filter}
            </button>
          );
        })}
      </div>

      {visiblePosts.length > 0 ? (
        <>
          <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {visiblePosts.map((post, index) => (
              <div key={post.slug} style={{ animation: "fadeIn 0.45s ease-out both", animationDelay: `${index * 90}ms` }}>
                <BlogPostCard
                  title={post.title}
                  description={post.description}
                  slug={post.slug}
                  category={post.category}
                  date={post.date}
                  readingTime={post.readingTime}
                  coverImage={post.coverImage}
                />
              </div>
            ))}
          </div>

          {hasMore && (
            <div className="mt-10 flex justify-center">
              <button
                type="button"
                onClick={() => setVisibleCount((current) => current + 6)}
                className="rounded-sm border border-accent/40 bg-accent/10 px-6 py-3 text-sm font-medium text-accent transition hover:border-accent hover:bg-accent/20"
              >
                Laadi rohkem
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="mt-10 rounded-sm border border-dashed border-white/10 bg-secondary/50 p-10 text-center text-text-muted">
          Selles kategoorias ei ole veel artikleid.
        </div>
      )}
    </>
  );
}
